import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sparkles, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RateAnalysisPanel({ contractId, contract, onScoreUpdate }) {
  const [analysis, setAnalysis] = useState(null);

  const analyzeMutation = useMutation({
    mutationFn: async () => {
      const token = localStorage.getItem('ccip_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/contracts/${contractId}/rate-analysis`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setAnalysis(data.analysis);
      if (onScoreUpdate) {
        onScoreUpdate(data.analysis.competitivenessScore);
      }
      toast.success('AI analysis complete!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    },
  });

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-700 bg-green-100';
    if (score >= 70) return 'text-blue-700 bg-blue-100';
    if (score >= 50) return 'text-yellow-700 bg-yellow-100';
    if (score >= 30) return 'text-orange-700 bg-orange-100';
    return 'text-red-700 bg-red-100';
  };

  const getRiskColor = (risk) => {
    const colors = {
      LOW: 'text-green-700 bg-green-50 border-green-200',
      MEDIUM: 'text-yellow-700 bg-yellow-50 border-yellow-200',
      HIGH: 'text-orange-700 bg-orange-50 border-orange-200',
      CRITICAL: 'text-red-700 bg-red-50 border-red-200',
    };
    return colors[risk] || colors.MEDIUM;
  };

  const getRecommendationIcon = (recommendation) => {
    if (recommendation === 'MONITOR') return <CheckCircle className="w-4 h-4" />;
    if (recommendation === 'PROACTIVE_OUTREACH') return <TrendingUp className="w-4 h-4" />;
    if (recommendation === 'PRIORITY_RENEWAL') return <AlertTriangle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4 text-red-600" />;
  };

  return (
    <Card className="border-2 border-[#146CBB]" data-testid="rate-analysis-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#09409C]" />
              AI Rate Analysis
            </CardTitle>
            <CardDescription className="mt-2">
              AI-powered competitiveness analysis and market insights
            </CardDescription>
          </div>
          <Button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            className="bg-gradient-to-r from-[#09409C] to-[#146CBB]"
            data-testid="analyze-button"
          >
            {analyzeMutation.isPending ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Run AI Analysis
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      {analysis && (
        <CardContent className="space-y-6">
          {/* Score Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-lg border-2 border-gray-200">
              <p className="text-sm text-muted-foreground mb-2">Competitiveness Score</p>
              <div className={`text-4xl font-bold ${getScoreColor(analysis.competitivenessScore)} rounded-lg py-2`}>
                {analysis.competitivenessScore}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Confidence: {analysis.confidence}%</p>
            </div>

            <div className="text-center p-4 rounded-lg border-2 border-gray-200">
              <p className="text-sm text-muted-foreground mb-2">Rate vs Market</p>
              <div className="flex items-center justify-center gap-2">
                {analysis.rateGapPercent > 0 ? (
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                ) : (
                  <TrendingDown className="w-6 h-6 text-green-600" />
                )}
                <span className={`text-3xl font-bold ${analysis.rateGapPercent > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                  {Math.abs(analysis.rateGapPercent).toFixed(1)}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {analysis.rateGapPercent > 0 ? 'Above' : 'Below'} market estimate
              </p>
            </div>

            <div className="text-center p-4 rounded-lg border-2 border-gray-200">
              <p className="text-sm text-muted-foreground mb-2">Risk Level</p>
              <Badge className={`${getRiskColor(analysis.riskLevel)} text-lg px-4 py-2`}>
                {analysis.riskLevel}
              </Badge>
              <p className="text-xs text-muted-foreground mt-2">
                Market Estimate: ${analysis.marketRateEstimate}/kWh
              </p>
            </div>
          </div>

          {/* Recommendation */}
          <Alert className="border-2 border-[#09409C] bg-blue-50">
            <div className="flex items-start gap-3">
              {getRecommendationIcon(analysis.recommendation)}
              <div className="flex-1">
                <AlertDescription className="text-base font-semibold text-[#09409C] mb-2">
                  Recommendation: {analysis.recommendation.replace(/_/g, ' ')}
                </AlertDescription>
                <p className="text-sm text-gray-700">{analysis.insights}</p>
              </div>
            </div>
          </Alert>

          {/* Top Factors */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Key Factors Influencing Score</h3>
            <div className="space-y-2">
              {analysis.topFactors.map((factor, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border">
                  <div className="flex-1">
                    <p className="font-medium">{factor.factor}</p>
                    <p className="text-sm text-muted-foreground">{factor.description}</p>
                  </div>
                  <Badge
                    variant="outline"
                    className={factor.impact > 0 ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-700'}
                  >
                    {factor.impact > 0 ? '+' : ''}{factor.impact}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Metadata */}
          <div className="text-xs text-muted-foreground text-center pt-4 border-t">
            Analysis powered by {analysis.model} • Generated at{' '}
            {new Date(analysis.scoredAt).toLocaleString()}
          </div>
        </CardContent>
      )}

      {!analysis && !analyzeMutation.isPending && (
        <CardContent>
          <div className="text-center py-12 text-muted-foreground">
            <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">No analysis available yet</p>
            <p className="text-sm">Click "Run AI Analysis" to get AI-powered insights</p>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
